import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActionSuivi, ActionSuiviStatut } from 'src/app/models/process/action-suivi.model';
import { ProcessChampConditionDetails, ProcessEnregistrementPayload, ProcessForLibrary, ProcessMailModelSelection, ProcessParametrage } from 'src/app/models/process/process-parametrage';
import { ConfigService } from '../config/config.service';

@Injectable({
  providedIn: 'root'
})
export class ProcessService {
  private configService = inject(ConfigService);
  private readonly baseUrlApi = this.configService.config.urlApi;

  constructor(private http: HttpClient) { }

  public getProcesses(search?: string, idEntite?: number | null): Observable<ProcessForLibrary[]> {
    let params = new HttpParams();
    const terme = search?.trim();
    if (terme) {
      params = params.set('search', terme);
    }
    if (idEntite != null) {
      params = params.set('idEntite', String(idEntite));
    }
    return this.http.get<ProcessForLibrary[]>(`${this.baseUrlApi}/api/pr/Process`, { params });
  }

  public getProcessesParametrage(): Observable<ProcessParametrage> {
    return this.http.get<ProcessParametrage>(`${this.baseUrlApi}/api/pr/ParametrageProcess`);
  }

  public getProcess(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrlApi}/api/pr/Process/${id}`);
  }

  public getChampConditionDetails(idChamp: number): Observable<ProcessChampConditionDetails> {
    return this.http.get<ProcessChampConditionDetails>(
      `${this.baseUrlApi}/api/pr/ParametrageProcess/champ/${idChamp}`
    );
  }

  public getMailModelSelection(entitesIds: number[], typeAction?: number): Observable<ProcessMailModelSelection[]> {
    let queryParams = new HttpParams();

    if (Array.isArray(entitesIds) && entitesIds.length > 0) {
      entitesIds.forEach((id) => {
        queryParams = queryParams.append('entiteIds', String(id));
      });
    }

    // IMPORTANT : aujourd’hui `typeAction` était ajouté uniquement dans la boucle,
    // donc si `entitesIds` est vide, tu n’avais plus aucun param.
    if (typeAction != null) {
      queryParams = queryParams.set('typeAction', String(typeAction));
    }

    return this.http.get<ProcessMailModelSelection[]>(
      `${this.baseUrlApi}/api/pr/ModelesMail`,
      { params: queryParams },
    );
  }

  public getActionsSuiviSession(idSession: number, planifiees: boolean): Observable<ActionSuivi[]> {
    const statutFilter = planifiees
      ? `Statut eq ${ActionSuiviStatut.Planifiee}`
      : `Statut ne ${ActionSuiviStatut.Planifiee}`;
    const filter = `(${statutFilter})and(IdSession eq ${idSession})`;
    const orderBy = planifiees ? 'Prevision desc' : 'Realisation desc';
    const url = `${this.baseUrlApi}/api/pr/ActionsSuivi?$filter=${filter}&$orderby=${orderBy}`;
    return this.http.get<any[] | { value: any[] }>(url).pipe(
      map((response) => {
        const items = Array.isArray(response) ? response : response?.value ?? [];
        return items.map((raw) => ({
          id: raw.IdAction ?? raw.id ?? raw.Id,
          nom: raw.Nom ?? raw.nom,
          prevision: raw.Prevision ?? raw.prevision ?? null,
          previsionHeure: raw.PrevisionHeure ?? raw.previsionHeure ?? null,
          realisation: raw.Realisation ?? raw.realisation ?? null,
          realisationHeure: raw.RealisationHeure ?? raw.realisationHeure ?? null,
          statut: raw.Statut ?? raw.statut,
          informations: raw.Informations ?? raw.informations ?? null,
          typeObjet: raw.TypeObjet ?? raw.typeObjet,
          idObjet: raw.IdObjet ?? raw.idObjet,
          automatisation: raw.Automatisation ?? raw.automatisation,
          nomFormation: raw.NomFormation ?? raw.nomFormation ?? null,
          debut: raw.Debut ?? raw.debut ?? null,
          fin: raw.Fin ?? raw.fin ?? null,
          typeTemporel: raw.TypeTemporel ?? raw.typeTemporel ?? raw.Type ?? raw.type,
          idSession: raw.IdSession ?? raw.idSession,
        } as ActionSuivi));
      }),
    );
  }

  public createProcess(process: ProcessEnregistrementPayload): Observable<ProcessEnregistrementPayload> {
    return this.http.post<ProcessEnregistrementPayload>(
      `${this.baseUrlApi}/api/pr/Process`,
      process
    );
  }

  public updateProcess(process: ProcessEnregistrementPayload): Observable<ProcessEnregistrementPayload> {
    return this.http.put<ProcessEnregistrementPayload>( `${this.baseUrlApi}/api/pr/Process`, process);
  }

  public deleteProcess(idProcess: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrlApi}/api/pr/Process/DeleteProcess/${idProcess}`);
  }

  public setProcessActivation(idProcess: number, actif: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrlApi}/api/pr/Process/DesactivatedProcess`, {
      id: idProcess,
      actif,
    });
  }

  public forcerAction(idAction: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrlApi}/api/pr/Actions/ForcerAction/${idAction}`, null);
  }

  public relancerAction(idAction: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrlApi}/api/pr/Actions/RelancerAction/${idAction}`, null);
  }
}
